import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { NavBar } from 'components/NavBar/NavBar'
import { Alert, AlertType } from 'components/Alert/Alert'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { ColoredDot, ColorVariant, Flex, H1WithBack, PageWrapper, TablesGrid, colors } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Card, CardBody } from 'components/Card/Card'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { Cell } from 'components/HonestTable/HonestTable.styled'
import { Input } from 'components/Input/Input'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { IFoodPerson } from 'types/foodLogistics/foodPerson.interface'
import { IFoodKitchen, IKitchenConfirmation } from 'types/foodLogistics/foodKitchen.interface'
import { IPlanAdjustment } from 'types/foodLogistics/mealWindow.interface'
import {
  foodInitialConfirmations,
  foodKitchenOrderedMock,
  foodKitchensMock,
  foodMealWindowsMock,
  foodPeopleMock,
} from '../foodData'
import { FoodDrawer, FoodDrawerOverlay } from '../Food.styled'
import { AdjustPlanModal } from '../components/AdjustPlanModal'
import { CancelMealModal } from '../components/CancelMealModal'
import { KitchenConfirmationModal } from '../components/KitchenConfirmationModal'

export const FoodWindowDetail = () => {
  const { id } = useParams()
  const windowId = Number(id)
  const w = foodMealWindowsMock.find((x) => x.id === windowId)

  const [kitchens, setKitchens] = useState<IFoodKitchen[]>(foodKitchensMock)
  const [people, setPeople] = useState<IFoodPerson[]>(foodPeopleMock)
  const [confirmations, setConfirmations] = useState<{ [key: string]: IKitchenConfirmation }>(foodInitialConfirmations)
  const [adjustments, setAdjustments] = useState<{ [windowId: number]: IPlanAdjustment[] }>({})
  const [selectedPerson, setSelectedPerson] = useState<IFoodPerson | null>(null)
  const [confKitchen, setConfKitchen] = useState<IFoodKitchen | null>(null)
  const [cancelFor, setCancelFor] = useState<IFoodPerson | null>(null)
  const [adjustOpen, setAdjustOpen] = useState(false)
  const [search, setSearch] = useState('')


  const kName = (kid: number) => kitchens.find((k) => k.id === kid)?.name
  const rows = people.filter((p) => p.status[w.id]).filter((p) => p.name.toLowerCase().includes(search.toLowerCase()))
  const manual = (adjustments[w.id] || []).filter((a) => !a.routedToStandby).reduce((s, a) => s + a.qty, 0)

  const submitConfirmation = (kitchenId: number, served: number, standbyUsed: number, onBehalf: boolean) => {
    setConfirmations((c) => ({ ...c, [`${w.id}-${kitchenId}`]: { served, standbyUsed, onBehalf } }))
    setKitchens((ks) =>
      ks.map((k) =>
        k.id === kitchenId
          ? {
            ...k,
            standby: k.standby.map((s, i) => (i === 0 ? { ...s, qty: Math.max(0, s.qty - standbyUsed) } : s)),
          }
          : k,
      ),
    )
    toast.success(`${kName(kitchenId)} confirmed: ${served} served, ${standbyUsed} standby used${onBehalf ? ' (on behalf)' : ''}`)
  }

  const addAdjustment = (_id: number, lines: IPlanAdjustment[], reason: string, locked: boolean) => {
    const entries = lines.map((l) => ({ ...l, qty: Number(l.qty), reason, routedToStandby: locked }))
    setAdjustments((a) => ({ ...a, [w.id]: [...(a[w.id] || []), ...entries] }))
    const tot = entries.reduce((s, e) => s + e.qty, 0)
    toast.success(
      locked
        ? `+${tot} routed to standby reserve (cut-off passed)`
        : `Plan adjusted +${tot} — MANUAL_ADJUSTMENT records created`,
    )
  }

  const handleSingleCancel = (reason: string) => {
    if (!cancelFor) return
    setPeople((ps) =>
      ps.map((x) =>
        x.id === cancelFor.id
          ? {
            ...x,
            status: { ...x.status, [w.id]: 'Cancelled' },
            chain: [
              ...x.chain,
              w.locked
                ? `Superseded → CANCELLED post cut-off · expected waste · ${reason}`
                : `Superseded → CANCELLED pre cut-off · ${reason}`,
            ],
          }
          : x,
      ),
    )
    toast.success(`${cancelFor.name} ${w.name.toLowerCase()} intent cancelled${w.locked ? ' — expected waste logged' : ''}`)
    setCancelFor(null)
    setSelectedPerson(null)
  }

  const peopleColumns: IHonestTableColumn<IFoodPerson>[] = [
    {
      id: 'name',
      header: 'Name',
      sticky: true,
      minWidth: '12rem',
      cell: (p) => (
        <Cell>
          <Flex gap='0.5rem' center>
            {p.flag && w.id === 1 && <ColoredDot color={colors.red} />}
            <BodyText weight='600'>{p.name}</BodyText>
          </Flex>
        </Cell>
      ),
    },
    { id: 'dept', header: 'Department', cell: (p) => <BodyText>{p.dept}</BodyText> },
    { id: 'mode', header: 'Mode', cell: (p) => <BodyText>{p.mode}</BodyText> },
    { id: 'source', header: 'Source', cell: (p) => <BodyText>{p.source}</BodyText> },
    { id: 'kitchen', header: 'Kitchen', cell: (p) => <BodyText>{kName(p.kitchenId)}</BodyText> },
    {
      id: 'dietary',
      header: 'Dietary',
      cell: (p) => (
        <Flex wrap gap='0.25rem'>
          {p.tags.map((t) => (
            <StatusPill key={t} status='accent'>{t}</StatusPill>
          ))}
        </Flex>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (p) => {
        const st = p.status[w.id]!
        return (
          <StatusPill status={st === 'Served' || st === 'Ordered' ? 'success' : st === 'Planned' ? 'info' : st === 'Cancelled' ? 'danger' : 'accent'}>{st}</StatusPill>
        )
      },
    },
  ]

  return (
    <>
      <NavBar />
      <PageWrapper noFooter>
        <Flex between center width='100%'>
          <H1WithBack arrow title={w.name} link='/hr/food' />
          <Flex gap='0.5rem'>
            <Button variant='secondary' onClick={() => setAdjustOpen(true)}>
              Adjust Plan
            </Button>
            <Button variant='primary'>Export</Button>
          </Flex>
        </Flex>

        <Flex center gap='0.75rem' mt='8'>
          <StatusPill status={w.status === 'Served' || w.status === 'Ordered' || w.status === 'Finalized' ? 'success' : w.status === 'Planned' ? 'info' : w.status === 'Cancelled' ? 'danger' : 'accent'}>{w.status}</StatusPill>
          <OtherText grey>
            {w.time} · Cut-off {w.cutoff}
            {w.locked && <OtherText color={colors.red}> (Passed — per-kitchen hot orders locked)</OtherText>}
          </OtherText>
        </Flex>

        {manual > 0 && (
          <Alert
            mt='1rem'
            title={`Plan includes manual adjustment: derived ${w.planned} + ${manual} manual`}
            description={`${(adjustments[w.id] || [])
              .map((a) => `${a.tag} ×${a.qty} @ ${kName(a.kitchenId)}${a.routedToStandby ? ' (standby)' : ''}`)
              .join(', ')} · reason: ${(adjustments[w.id] || [])[0]?.reason}`}
            type={AlertType.WARNING}
            colorVariant={ColorVariant.PRIMARY}
          />
        )}

        <Flex column gap='1rem' width='100%' mt='16'>
          <Container>
            <ContainerHeader>
              <TitleText weight='700'>Menu — provided by Kitchen & Inventory (read-only)</TitleText>
            </ContainerHeader>
            <ContainerContent>
              <TablesGrid>
                {kitchens.map((k) => (
                  <Card key={k.id}>
                    <CardBody>
                      <BodyText weight='600'>{k.name}</BodyText>
                      <OtherText grey>{w.menus[k.id] || '—'}</OtherText>
                    </CardBody>
                  </Card>
                ))}
              </TablesGrid>
            </ContainerContent>
          </Container>

          <Container>
            <ContainerHeader>
              <TitleText weight='700'>Kitchen Confirmation — per kitchen</TitleText>
            </ContainerHeader>
            <ContainerContent gap='0.5rem'>
              {!w.locked ? (
                <OtherText grey>Opens after this window&apos;s cut-off ({w.cutoff}).</OtherText>
              ) : (
                kitchens.map((k) => {
                  const ord = foodKitchenOrderedMock[w.id]?.[k.id] || 0
                  if (ord === 0) return null
                  const c = confirmations[`${w.id}-${k.id}`]
                  return (
                    <Card key={k.id}>
                      <CardBody>
                        <Flex gap='0.75rem' center wrap>
                          <BodyText weight='700' style={{ minWidth: '9rem' }}>
                            {k.name}
                          </BodyText>
                          <OtherText grey>Ordered: {ord}</OtherText>
                          {c ? (
                            <Flex gap='0.5rem' center>
                              <StatusPill status='success'>{`${c.served} served · ${c.standbyUsed} standby used`}</StatusPill>
                              {c.onBehalf && (
                                <StatusPill status='accent'>entered on behalf — audited</StatusPill>
                              )}
                            </Flex>
                          ) : (
                            <Flex gap='0.5rem' center width='100%' between>
                              <StatusPill status='accent'>Awaiting confirmation</StatusPill>
                              <Button variant='primary' size='sm' onClick={() => setConfKitchen(k)}>
                                Enter Confirmation
                              </Button>
                            </Flex>
                          )}
                        </Flex>
                      </CardBody>
                    </Card>
                  )
                })
              )}
            </ContainerContent>
          </Container>

          <Container>
            <ContainerHeader padding>
              <Flex between center width='100%' wrap gap='0.75rem'>
                <TitleText weight='700'>Employee List</TitleText>
                <Input
                  placeholder='Search employee…'
                  value={search}
                  onChange={(e: any) => setSearch(e.target.value)}
                  nomarg
                  width='15rem'
                  search
                />
              </Flex>
            </ContainerHeader>
            <ContainerContent>
              <HonestTable
                columns={peopleColumns}
                data={rows}
                keyField='id'
                onClick={(p) => setSelectedPerson(p)}
              />
            </ContainerContent>
          </Container>
        </Flex>

        {selectedPerson &&
          (() => {
            const p = people.find((x) => x.id === selectedPerson.id)!
            const st = p.status[w.id]
            const isOrdered = st === 'Ordered' || st === 'Served'
            return (
              <>
                <FoodDrawerOverlay onClick={() => setSelectedPerson(null)} />
                <FoodDrawer>
                  <Flex between width='100%' center>
                    <TitleText weight='bold'>Person Details — {w.name}</TitleText>
                    <CloseIcon onClick={() => setSelectedPerson(null)} />
                  </Flex>
                  <Flex column gap='0.5rem' mt='16'>
                    <OtherText grey>NAME</OtherText>
                    <BodyText weight='700'>{p.name}</BodyText>
                  </Flex>
                  <TablesGrid>
                    {[
                      ['DEPARTMENT', p.dept],
                      ['SOURCE', p.source],
                      ['KITCHEN', kName(p.kitchenId)],
                      ['MODE', p.mode],
                    ].map(([l, v]) => (
                      <Flex key={l} column gap='0.25rem'>
                        <OtherText grey>{l}</OtherText>
                        <BodyText>{v}</BodyText>
                      </Flex>
                    ))}
                  </TablesGrid>
                  <Flex column gap='0.5rem' mt='16'>
                    <OtherText grey>DIETARY REQUIREMENT (tag set)</OtherText>
                    <Flex wrap gap='0.25rem'>
                      {p.tags.map((t) => (
                        <StatusPill key={t} status='accent'>{t}</StatusPill>
                      ))}
                    </Flex>
                  </Flex>
                  <Flex mt='16'>
                    <StatusPill status={st === 'Served' || st === 'Ordered' ? 'success' : st === 'Planned' ? 'info' : st === 'Cancelled' ? 'danger' : 'accent'}>{st}</StatusPill>
                  </Flex>
                  {p.flag && w.id === 1 && (
                    <Alert
                      mt='1rem'
                      title='Dietary hard stop'
                      description={`{${p.tags.join(', ')}} ⊄ Main Kitchen capabilities. Resolve via Exceptions.`}
                      type={AlertType.WARNING}
                      colorVariant={ColorVariant.PRIMARY}
                    />
                  )}
                  <Flex gap='0.5rem' mt='16'>
                    <Button variant='secondary' disabled={isOrdered}>
                      Assign Standby
                    </Button>
                    <Button variant='primary' onClick={() => setCancelFor(p)}>
                      Cancel Meal Intent
                    </Button>
                  </Flex>
                  <Flex column gap='0.25rem' mt='16'>
                    <OtherText grey>RECORD CHAIN (append-only)</OtherText>
                    {p.chain.map((c, i) => (
                      <OtherText key={i} grey>{c}</OtherText>
                    ))}
                  </Flex>
                </FoodDrawer>
              </>
            )
          })()}

        {adjustOpen && (
          <AdjustPlanModal
            window={w}
            kitchens={kitchens}
            manual={manual}
            addAdjustment={addAdjustment}
            closeModal={() => setAdjustOpen(false)}
          />
        )}

        {cancelFor && (
          <CancelMealModal
            title='Cancel Meal Intent'
            description={`Cancel ${cancelFor.name}'s ${w.name.toLowerCase()} at ${kName(cancelFor.kitchenId)}?`}
            locked={w.locked}
            closeModal={() => setCancelFor(null)}
            onConfirm={handleSingleCancel}
          />
        )}

        {confKitchen && (
          <KitchenConfirmationModal
            kitchenName={confKitchen.name}
            windowName={w.name}
            ordered={foodKitchenOrderedMock[w.id]?.[confKitchen.id] || 0}
            closeModal={() => setConfKitchen(null)}
            onSubmit={(served, standbyUsed, onBehalf) => {
              submitConfirmation(confKitchen.id, served, standbyUsed, onBehalf)
              setConfKitchen(null)
            }}
          />
        )}
      </PageWrapper>
    </>
  )
}
